import { BREAKPOINTS, LoadingIndicator, ModalDialog, PageWidth, Size, SystemIcons } from "@laerdal/life-react-components";
import axios from "axios";
import { lazy, Suspense, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { KnowledgeArticleDto } from "../../model/dto/knowledgeArticleDto";
import ArticleApi from "../../services/api/ArticleApi";
import { KnowledgeArticleListItemDto } from "../../model/dto/knowledgeArticleListItemDto";
import posthog from 'posthog-js';
import CantFindCreateCase from "../../commonComponents/CantFindCreateCase";
import ArticleFeedback from "../../commonComponents/ArticleFeedback";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { buildMarketPath, selectMarket, selectTranslateWithAi } from "../../store/slices/globalConfigSlice";
import { marketList } from "../../model/constants/marketContants";
import TranslateWithAI from "../../commonComponents/TranslateWithAI";
import { useMediaMatch } from "rooks";
import {
  ContentColumn,
  ArticleHtmlContainer,
  ArticleFilesContainer,
  FilesTitle,
  FileItem,
  FileIcon,
  FileLink,
  FileSize,
  LoadingIndicatorContainer,
  ArticlePageContainer,
  BreadcrumbContainer,
  ArticleTitle,
  CrumbLink,
  BreadcrumbDivider,
  RelatedArticlesContainer,
  ArticleMeta,
  RelatedArticlesTitle,
  RelatedArticlesList,
  RelatedArticleLink,
  AttachmentContainer,
  ImagePreview,
} from "./ArticlePage.styles";

// pdfjs-dist is ~1MB; load the viewer only when a PDF preview is actually opened
const PdfPreview = lazy(() => import("../../commonComponents/PdfPreview"));

const getReadTime = (html?: string): number => {
  if (!html) return 0;
  const text = html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  const wordCount = text.split(/\s+/).filter(w => w.length > 0).length;
  return Math.max(1, Math.ceil(wordCount / 238));
};

// Helper function to format file size
const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// Helper function to check if file is an image
const isImageFile = (fileName: string): boolean => {
  const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp', '.svg'];
  return imageExtensions.some(ext => fileName.toLowerCase().endsWith(ext));
};

const ArticlePage = () => {
  const { articleNumber, title } = useParams<{ product: string, category: string, articleNumber: string, title: string }>();
  const { t, i18n } = useTranslation(['articles', 'translation', 'landingPage']);

  const navigate = useNavigate()
  const location = useLocation();
  const [article, setArticle] = useState<KnowledgeArticleDto | undefined>(undefined);
  const [loadingArticle, setLoadingArticle] = useState<boolean>(true);
  const [articleNotFound, setArticleNotFound] = useState<boolean>(false);
  const [articles, setArticles] = useState<KnowledgeArticleListItemDto[]>([]);
  const [articlesLoading, setArticlesLoading] = useState<boolean>(true);

  const currentMartket = useSelector(selectMarket);
  const translateWithAi = useSelector(selectTranslateWithAi);

  const isMediumScreen = useMediaMatch(BREAKPOINTS.MEDIUM.replace('@media ', ''));
  const isMobile = !isMediumScreen;

  const [pdfPreviewModalOpen, setPdfPreviewModalOpen] = useState<boolean>(false);
  const [pdfPreviewLink, setPdfPreviewLink] = useState<string>('');
  const [pdfPreviewFileName, setPdfPreviewFileName] = useState<string>('');
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const downloadPdfFile = async (url: string, fileName: string) => {
    setIsDownloading(true);
    try {
      // Fetch the file as a blob to handle cross-origin downloads
      const response = await fetch(url);
      if (!response.ok) throw new Error('Network response was not ok');

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      // Create temporary anchor and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = fileName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      // Fallback: open in new tab if download fails
      window.open(url, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    const controller = new AbortController();
    const signal = controller.signal;

    setArticle(undefined);
    setArticles([]);
    setLoadingArticle(true);
    setArticlesLoading(true);
    setArticleNotFound(false);

    ArticleApi.GetKnowledgeArticle(articleNumber ?? '', title, undefined, translateWithAi ? marketList[currentMartket].locale : undefined, signal)
      .then((data) => {
        if (!data) {
          setArticleNotFound(true);
          setLoadingArticle(false);
          setArticlesLoading(false);
          return;
        }

        if (!!data.publicContent) {
          if (!!currentMartket && currentMartket != 'en') {
            data.publicContent = data.publicContent.replaceAll(`href="/`, `href="/${currentMartket}/`);
            data.publicContent = data.publicContent.replaceAll("{{currentURL}}", location.pathname)
          }
          else {
            data.publicContent = data.publicContent.replaceAll("{{currentURL}}", location.pathname);
          }
        }

        setArticle(data);
        setLoadingArticle(false);
        ArticleApi.RegisterView(data.articleNumber);
        posthog.capture('ArticleView', {
          currentArticle: articleNumber,
          version: 'web',
          productLine: data.productLine,
          primaryProductLine: data.primaryProductLine
        });

        ArticleApi.GetKnowledgeArticles(6, 0, data.title, '', signal, translateWithAi ? marketList[currentMartket].locale : undefined)
          .then((data) => {
            setArticles(data.items.filter(x => x.articleNumber != articleNumber));
            setArticlesLoading(false);
          })
          .catch((error) => {
            if (axios.isCancel(error)) return;
          });
      })
      .catch((error) => {
        if (axios.isCancel(error)) return;
      });

    return () => {
      controller.abort();
    };
  }, [articleNumber, translateWithAi]);

  // Intercept clicks on in-app `/articles/...` links inside the rendered HTML so navigation
  // stays inside the SPA (no full page reload). External links, modifier-clicks, target="_blank"
  // and any non-article paths fall through to default browser behaviour.
  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;

    const anchor = (e.target as HTMLElement | null)?.closest?.('a');
    if (!anchor) return;
    if (anchor.target === '_blank' || anchor.hasAttribute('download')) return;

    const href = anchor.getAttribute('href');
    if (!href) return;

    // In-page anchor links (`#section`) resolve against the <base href="/"> tag and would
    // navigate to the homepage — scroll to the target element on this page instead.
    if (href.startsWith('#')) {
      e.preventDefault();
      let id = href.slice(1);
      if (!id) return;
      try { id = decodeURIComponent(id); } catch { /* keep raw id */ }
      const el = document.getElementById(id)
        ?? document.querySelector(`a[name="${CSS.escape(id)}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      // Preserve pathname/search explicitly: navigate({ hash }) alone would clear the query string.
      navigate({ pathname: location.pathname, search: location.search, hash: href }, { replace: true });
      return;
    }

    // Only intercept root-relative article links (with or without a 2-char market prefix).
    if (!/^\/(?:[a-z]{2}\/)?articles\//.test(href)) return;

    e.preventDefault();
    posthog.capture('SUPP-ArticlePage InContentLinkClick', {
      currentArticle: articleNumber,
      target: href,
    });
    navigate(href);
  };

  // When the URL contains a hash (deep-link to a sub-section anchor produced by the Paligo
  // link rewriter, e.g. `#UUID-{uuid}_section-id...`), scroll the corresponding DOM element
  // into view once the article content has rendered.
  useEffect(() => {
    if (!article?.publicContent) return;
    if (!location.hash) return;

    let id = location.hash.slice(1);
    if (!id) return;
    try { id = decodeURIComponent(id); } catch { /* keep raw id */ }

    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [article?.publicContent, location.hash]);

  return (
    <>
      <ModalDialog
        isModalOpen={pdfPreviewModalOpen}
        closeAction={() => {
          posthog.capture('SUPP-ArticlePage-PreviewClosed', {
            currentArticle: articleNumber,
            fileName: pdfPreviewFileName,
          });
          setPdfPreviewModalOpen(false);
        }}
        submitAction={(e) => e?.preventDefault()}
        size={isMobile ? Size.Small : Size.Large}
        title={pdfPreviewFileName}
        contentOverflow={'auto'}
        buttons={[
          {
            id: 'download',
            variant: 'tertiary',
            text: t('Download', { ns: 'landingPage' }),
            action: async () => {
              posthog.capture('SUPP-ArticlePage FileDownloadFromPreview', {
                currentArticle: articleNumber,
                fileName: pdfPreviewFileName,
                fileUrl: pdfPreviewLink
              });
              await downloadPdfFile(pdfPreviewLink, pdfPreviewFileName);
            },
            loading: isDownloading,
            disabled: isDownloading,
            type: 'button',
          },
          {
            id: 'close',
            variant: 'primary',
            type: 'button',
            text: t('Close', { ns: 'landingPage' }),
            action: () => {
              posthog.capture('SUPP-ArticlePage-PreviewClosed', {
                currentArticle: articleNumber,
                fileName: pdfPreviewFileName,
              });
              setPdfPreviewModalOpen(false);
            },
          },
        ]}>
        <AttachmentContainer>
          {isImageFile(pdfPreviewFileName) ? (
            <ImagePreview src={pdfPreviewLink} alt={pdfPreviewFileName} />
          ) : (
            pdfPreviewModalOpen && (
              <Suspense fallback={<LoadingIndicator />}>
                <PdfPreview
                  fileUrl={pdfPreviewLink}
                  pageWidth={isMobile ? 300 : 800}
                  onFallbackDownload={() => {
                    posthog.capture('SUPP-ArticlePage-PdfFallbackDownload', {
                      currentArticle: articleNumber,
                      fileName: pdfPreviewFileName,
                    });
                  }}
                />
              </Suspense>
            )
          )}
        </AttachmentContainer>
      </ModalDialog>

      <ArticlePageContainer>
        <PageWidth $useMaxWidth={true} $maxWidth={1024} style={{ width: "calc(100% - 32px)" }}>
          

          <BreadcrumbContainer>
            <CrumbLink
              href={`/${currentMartket}`}
              onClick={(e) => {
                e.preventDefault();
                posthog.capture('SUPP-BreadcrumbClick', { currentUrl: window.location.href, clickedLink: `/${currentMartket}`, step: 1 });
                navigate(`/${currentMartket}`);
              }}
            >
              {t('Home', { ns: 'landingPage' })}
            </CrumbLink>
            {article?.categoryName && article?.categoryLink && (
              <>
                <BreadcrumbDivider>/</BreadcrumbDivider>
                <CrumbLink
                  href={buildMarketPath(currentMartket, `/category/${article.categoryLink}`)}
                  onClick={(e) => {
                    e.preventDefault();
                    const link = buildMarketPath(currentMartket, `/category/${article.categoryLink}`);
                    posthog.capture('SUPP-BreadcrumbClick', { currentUrl: window.location.href, clickedLink: link, step: 2 });
                    navigate(link);
                  }}
                >
                  {t(article.categoryName, { ns: 'landingPage' })}
                </CrumbLink>
              </>
            )}
            {article?.categoryLink && article?.primaryProductLineName && article?.primaryProductLine && (
              <>
                <BreadcrumbDivider>/</BreadcrumbDivider>
                <CrumbLink
                  href={buildMarketPath(currentMartket, `/category/${article.categoryLink}/product/${article.primaryProductLine}`)}
                  onClick={(e) => {
                    e.preventDefault();
                    const link = buildMarketPath(currentMartket, `/category/${article.categoryLink}/product/${article.primaryProductLine}`);
                    posthog.capture('SUPP-BreadcrumbClick', { currentUrl: window.location.href, clickedLink: link, step: 3 });
                    navigate(link);
                  }}
                >
                  {article.primaryProductLineName}
                </CrumbLink>
              </>
            )}
          </BreadcrumbContainer>

          <ContentColumn>
            {loadingArticle &&
              <LoadingIndicatorContainer>
                <LoadingIndicator />
              </LoadingIndicatorContainer>
            }

            {!loadingArticle && articleNotFound &&
              <h3 id="no-article-message">{t('No article', { ns: 'articles', defaultValue: 'No article' })}</h3>
            }

            {!loadingArticle && !articleNotFound &&
              <>
                {marketList[currentMartket].locale != 'en' && <TranslateWithAI isMedicalProduct={article?.isMedical} />}
                {article && <ArticleTitle>{article.title}</ArticleTitle>}
                {article &&
                  <ArticleHtmlContainer
                    onClick={handleContentClick}
                    dangerouslySetInnerHTML={{ __html: article.publicContent ?? '' }} />
                }

                {article &&
                  <ArticleMeta>
                    <span>{getReadTime(article.publicContent)} {t('min read', { ns: 'articles' })}</span>
                    {article.updatedAt && <><SystemIcons.Bullet /><span>{new Date(article.updatedAt).toLocaleDateString(i18n.language, { month: 'short', day: 'numeric', year: 'numeric' })}</span></>}
                    {article.viewCount != null && <><SystemIcons.Bullet /><span>{article.viewCount} {t('views', { ns: 'articles' })}</span></>}
                  </ArticleMeta>
                }

                {article && <ArticleFeedback articleNumber={article.articleNumber} positiveFeedbackCount={article.positiveFeedbackCount} productLineName={article.primaryProductLineName} productLineCode={article.primaryProductLine} />}

                {!articlesLoading && articles.length > 0 &&
                  <RelatedArticlesContainer>
                    <RelatedArticlesTitle>
                      {t('Related articles')}
                    </RelatedArticlesTitle>
                    <RelatedArticlesList>
                      {articles.map(item =>
                      (
                        <RelatedArticleLink
                          key={`article-${item.articleNumber}`}
                          href={`/articles/${item.articleNumber}`}
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            posthog.capture('ArticlePage OpenRelatedArticle', {
                              currentArticle: articleNumber,
                              relatedArticle: item.articleNumber,
                            });
                            navigate(`/articles/${item.articleNumber}`)
                          }}
                        >
                          {item.title}
                        </RelatedArticleLink>
                      ))
                      }
                    </RelatedArticlesList>
                  </RelatedArticlesContainer>
                }

                {article && article.files && article.files.length > 0 && (
                  <>
                    <FilesTitle>{t('Attachments')}</FilesTitle>

                    <ArticleFilesContainer>
                      {article.files.map((file, index) => {
                        const isPdf = file.fileName.toLowerCase().endsWith('.pdf');
                        const isImage = isImageFile(file.fileName);
                        const canPreview = isPdf || isImage;
                        return (
                          <FileItem key={index}>
                            <FileIcon>
                              <SystemIcons.Link />
                            </FileIcon>
                            {canPreview ? (
                              <FileLink
                                href={file.fileUrl}
                                onClick={(e) => {
                                  e.preventDefault();
                                  posthog.capture('SUPP-ArticlePage FilePreview', {
                                    currentArticle: articleNumber,
                                    fileName: file.fileName,
                                    fileSize: file.fileSizeBytes
                                  });
                                  setPdfPreviewModalOpen(true);
                                  setPdfPreviewLink(file.fileUrl);
                                  setPdfPreviewFileName(file.fileName);
                                }}
                              >
                                {file.fileName}
                              </FileLink>
                            ) : (
                              <FileLink
                                href={file.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => {
                                  posthog.capture('SUPP-ArticlePage FileDownload', {
                                    currentArticle: articleNumber,
                                    fileName: file.fileName,
                                    fileSize: file.fileSizeBytes
                                  });
                                }}
                              >
                                {file.fileName}
                              </FileLink>
                            )}
                            <FileSize>({formatFileSize(file.fileSizeBytes)})</FileSize>
                          </FileItem>
                        );
                      })}
                    </ArticleFilesContainer>
                  </>
                )}
              </>
            }

          </ContentColumn>
          <CantFindCreateCase />
        </PageWidth>
      </ArticlePageContainer>


    </>);
};

export default ArticlePage;
